import { makeAutoObservable } from 'mobx';

import {
  type ApiLog,
  type RawApiLog,
  transformRawApiLogToApiLog,
} from './api-logs.types';

class ApiLogsStore {
  logs: ApiLog[] = [];
  paused = false;
  pageActive = false;
  searchTerm = '';
  selectedApps: string[] = [];
  selectedTags: string[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  get currentLimit() {
    return this.pageActive ? 1000 : 50;
  }

  get availableApps(): string[] {
    const apps = new Set<string>();
    this.logs.forEach((log) => apps.add(log.app));
    return Array.from(apps);
  }

  get availableTags(): string[] {
    const tags = new Set<string>();
    this.logs.forEach((log) => log.tags.forEach((tag) => tags.add(tag)));
    return Array.from(tags);
  }

  get filteredLogs(): ApiLog[] {
    return this.logs.filter((log) => {
      const matchesSearch =
        this.searchTerm === '' ||
        log.url.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        log.app.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        (log.requestBody &&
          JSON.stringify(log.requestBody)
            .toLowerCase()
            .includes(this.searchTerm.toLowerCase())) ||
        (log.responseBody &&
          JSON.stringify(log.responseBody)
            .toLowerCase()
            .includes(this.searchTerm.toLowerCase())) ||
        (log.errorMessage &&
          log.errorMessage
            .toLowerCase()
            .includes(this.searchTerm.toLowerCase()));

      const matchesApp =
        this.selectedApps.length === 0 || this.selectedApps.includes(log.app);

      const matchesTags =
        this.selectedTags.length === 0 ||
        log.tags.some((tag) => this.selectedTags.includes(tag));

      return matchesSearch && matchesApp && matchesTags;
    });
  }

  addLogs(rawLog: RawApiLog) {
    if (this.paused) return;
    const transformed = transformRawApiLogToApiLog(rawLog);
    transformed.forEach((log) => this.logs.push({ ...log }));
    if (this.logs.length > this.currentLimit) {
      this.logs = this.logs.slice(-this.currentLimit);
    }
  }

  trimToLimit() {
    if (this.logs.length > this.currentLimit) {
      this.logs = this.logs.slice(-this.currentLimit);
    }
  }

  setPaused(v: boolean) {
    this.paused = v;
  }

  setPageActive(v: boolean) {
    this.pageActive = v;
    this.trimToLimit();
  }

  setSearchTerm(v: string) {
    this.searchTerm = v;
  }

  setSelectedApps(v: string[]) {
    this.selectedApps = v;
  }

  setSelectedTags(v: string[]) {
    this.selectedTags = v;
  }

  clearLogs() {
    this.logs = [];
  }
}

export const apiLogsStore = new ApiLogsStore();
